import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'
import { nanoid } from 'nanoid'

import { RoutingPaths } from 'app/routes/routes.ts'

import { preparedDataForSpecialTour } from 'entities/tour/libs/preparedDataForSpecialTour.tsx'
import searchTour from 'entities/tour/models/search-tour/search-tour.ts'
import specialTourStore from 'entities/tour/models/special-tour/special-tour.ts'
import tourListStore from 'entities/tour/models/tour-list/tour-list.ts'
import { SpecialTourListSkeleton } from 'entities/tour/ui/special-tours/components/special-tour-list-skeleton'
import { SpecialToursCardMini } from 'entities/tour/ui/special-tours/components/special-tours-card-mini'
import { useSpecialTours } from 'entities/tour/ui/special-tours/libs/useSpecialTours.ts'

import { SpecialTour } from 'shared/api/tours/search'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { TagsFilter } from 'shared/ui/tags-filter'

import s from './styles.module.scss'

const SpecialToursPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [activeTag, setActiveTag] = useState<string | null>(null)

  const specialToursData = specialTourStore.getSpecialTourList()
  const isLoading = specialTourStore.isLoading

  const { tours, categories } = useSpecialTours(
    activeTag ?? specialToursData?.[0]?.id,
    specialToursData,
  )

  const fetchSpecialTours = async () => {
    if (!specialToursData?.length)
      void specialTourStore.requestSpecialToursList()

    tourListStore.resetTourList()
  }

  const onClickTour = (tour: SpecialTour) => {
    const preparedData = preparedDataForSpecialTour(tour)

    searchTour.setDataForSearchTour(preparedData)

    navigate(RoutingPaths.tours.searchResults.path())
  }

  const onGoBack = () => {
    navigate(RoutingPaths.tours.path())
  }

  const onTagClick = (id: string) => {
    setActiveTag(id)
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    void fetchSpecialTours()
  }, [])

  return (
    <div className={s.container}>
      <HeaderGoBack
        buttonWhite
        variant="grey"
        title={t('tours_block.best_offers')}
        onGoBack={onGoBack}
      />

      {isLoading ? (
        <SpecialTourListSkeleton />
      ) : (
        <>
          <div className={s.tags}>
            <TagsFilter
              className={s.tag}
              tags={categories}
              defaultActive={categories?.[0]?.id}
              onClick={onTagClick}
            />
          </div>

          <div className={s.toursList}>
            {tours?.map((tour) => (
              <SpecialToursCardMini
                key={nanoid()}
                data={tour}
                onClick={() => onClickTour(tour)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default observer(SpecialToursPage)
